import { useCallback, useEffect, useState } from "react";
import { fetchPortalPassword } from "../../../../../shared/api/postCall/fetchPortalPassword";
import { useAbortableFetch } from "../../../../hooks/useAbortableFetch";
import { CredentialWithId } from "../../../../pages/credentialsConfigration/types/credentialTypes";
import { encryptPassword } from "../../../../utils/SimplePasswordCrypto";

export interface CredentialFormData {
  portal_name: string;
  portal_url: string;
  facility_name: string;
  /** OncoEMR locationId; required only when portal_id is oncoemr. */
  location_id: string;
  username: string;
  password: string;
  secret_key: string;
  mfa_present: string;
  is_queue: string;
  is_active: string;
  is_available: string;
  email: string;
  body_regex: string;
  subject_regex: string;
  portal_id: string;
  /** Cap on concurrent sessions; only relevant when MFA is off. Stored as a
   *  string to match the rest of the form; coerced to a number on save. */
  max_concurrent: string;
}

/** Default Max Concurrency applied to new credentials and edit-mode docs
 *  that predate the field. */
const DEFAULT_MAX_CONCURRENCY = "30";

const initialFormData: CredentialFormData = {
  portal_name: "",
  portal_url: "",
  facility_name: "",
  location_id: "",
  username: "",
  password: "",
  secret_key: "",
  mfa_present: "",
  is_queue: "",
  is_active: "",
  is_available: "",
  email: "",
  body_regex: "",
  subject_regex: "",
  portal_id: "",
  max_concurrent: DEFAULT_MAX_CONCURRENCY,
};

interface UseCredentialFormProps {
  isEditMode: boolean;
  credentialData: CredentialWithId | null;
  isOpen: boolean;
}

/**
 * Custom hook for managing credential form state and operations.
 *
 * This hook handles:
 * - Form state initialization and updates
 * - Password and secret key editing with secure handling
 * - Fetching existing encrypted values in edit mode
 * - Encryption of sensitive fields before submission
 * - Synchronization between MFA and queue fields
 *
 * @param props - The hook configuration props
 * @param props.isEditMode - Whether the form is in edit mode
 * @param props.credentialData - Existing credential data for edit mode
 * @param props.isOpen - Whether the modal is currently open
 *
 * @returns An object containing:
 * - `formData` - Current form state
 * - `isPasswordEditing` - Whether password field is being edited
 * - `isSecretKeyEditing` - Whether secret key field is being edited
 * - `handleInputChange` - Function to handle form field changes
 * - `handlePasswordEditClick` - Function to enable password editing
 * - `handleSecretKeyEditClick` - Function to enable secret key editing
 * - `getPasswordForSubmission` - Async function to get encrypted password for submission
 * - `getSecretKeyForSubmission` - Async function to get encrypted secret key for submission
 *
 * @example
 * ```tsx
 * const {
 *   formData,
 *   handleInputChange,
 *   getPasswordForSubmission,
 * } = useCredentialForm({
 *   isEditMode: false,
 *   credentialData: null,
 *   isOpen: true,
 * });
 * ```
 */
export const useCredentialForm = ({
  isEditMode,
  credentialData,
  isOpen,
}: UseCredentialFormProps) => {
  const [formData, setFormData] = useState<CredentialFormData>(initialFormData);
  const [isPasswordEditing, setIsPasswordEditing] = useState(false);
  const [isSecretKeyEditing, setIsSecretKeyEditing] = useState(false);
  const [existingEncryptedPassword, setExistingEncryptedPassword] =
    useState("");
  const [existingEncryptedSecretKey, setExistingEncryptedSecretKey] =
    useState("");

  // Abortable fetch for password
  const fetchPasswordFn = useCallback(
    (signal: AbortSignal, credentialId: string) =>
      fetchPortalPassword(credentialId, signal),
    [],
  );
  const {
    runFetch: runFetchPassword,
    abort: abortPasswordFetch,
    loading: passwordLoading,
  } = useAbortableFetch(fetchPasswordFn);

  // Abortable fetch for secret key
  const fetchSecretKeyFn = useCallback(
    (signal: AbortSignal, secretKeyId: string) =>
      fetchPortalPassword(secretKeyId, signal),
    [],
  );
  const {
    runFetch: runFetchSecretKey,
    abort: abortSecretKeyFetch,
    loading: secretKeyLoading,
  } = useAbortableFetch(fetchSecretKeyFn);

  // Fetch existing password for edit mode
  const fetchExistingPassword = async () => {
    if (isEditMode && credentialData?.credential_id) {
      const result = await runFetchPassword(credentialData.credential_id);
      if (result.success && result.data) {
        setExistingEncryptedPassword(result.data.secret_value);
      } else if (result.error?.name !== "AbortError") {
        console.error("Error fetching existing password:", result.error);
      }
    }
  };

  // Fetch existing secret key for edit mode
  const fetchExistingSecretKey = async () => {
    if (isEditMode && credentialData?.secret_key) {
      const result = await runFetchSecretKey(credentialData.secret_key);
      if (result.success && result.data) {
        setExistingEncryptedSecretKey(result.data.secret_value);
      } else if (result.error?.name !== "AbortError") {
        console.error("Error fetching existing secret key:", result.error);
      }
    }
  };

  // Initialize form data based on edit mode
  useEffect(() => {
    if (isEditMode && credentialData) {
      setFormData({
        portal_name: credentialData.portal_name || "",
        portal_url: credentialData.portal_url || "",
        facility_name: credentialData.facility_name || "",
        location_id: credentialData.location_id || "",
        username: credentialData.username || "",
        password: "",
        secret_key: "",
        mfa_present: credentialData.is_queue ? "true" : "false",
        is_queue: credentialData.is_queue ? "true" : "false",
        is_active: credentialData.is_active ? "true" : "false",
        is_available: credentialData.is_available ? "true" : "false",
        email: credentialData.email || "",
        body_regex: credentialData.body_regex || "",
        subject_regex: credentialData.subject_regex || "",
        portal_id: credentialData.portal_id || "",
        max_concurrent:
          credentialData.max_concurrent != null
            ? String(credentialData.max_concurrent)
            : DEFAULT_MAX_CONCURRENCY,
      });
      setIsPasswordEditing(false);
      setIsSecretKeyEditing(false);
    } else {
      setFormData(initialFormData);
      setIsPasswordEditing(false);
      setIsSecretKeyEditing(false);
    }
  }, [isEditMode, credentialData]);

  // Fetch existing encrypted values when modal opens in edit mode
  useEffect(() => {
    if (isOpen && isEditMode) {
      fetchExistingPassword();
      fetchExistingSecretKey();
    }

    // Abort pending fetches when modal closes or component unmounts
    return () => {
      abortPasswordFetch();
      abortSecretKeyFetch();
    };
  }, [isOpen, isEditMode]);

  // Handle input changes with MFA/queue sync
  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "mfa_present" && {
        is_queue: value === "true" ? "true" : "false",
      }),
      ...(field === "is_queue" && {
        mfa_present: value === "true" ? "true" : "false",
      }),
    }));
  };

  // Handle password edit click
  const handlePasswordEditClick = () => {
    setIsPasswordEditing(true);
    setFormData((prev) => ({ ...prev, password: "" }));
  };

  // Handle secret key edit click
  const handleSecretKeyEditClick = () => {
    setIsSecretKeyEditing(true);
    setFormData((prev) => ({ ...prev, secret_key: "" }));
  };

  // Get password value for submission (handles both add and edit modes)
  const getPasswordForSubmission = async (): Promise<string> => {
    if (isEditMode) {
      if (
        isPasswordEditing &&
        formData.password &&
        formData.password.trim() !== ""
      ) {
        try {
          return await encryptPassword(formData.password);
        } catch (encryptionError) {
          console.error("Error encrypting password:", encryptionError);
          throw encryptionError;
        }
      } else {
        return existingEncryptedPassword;
      }
    } else {
      if (formData.password && formData.password.trim() !== "") {
        try {
          return await encryptPassword(formData.password);
        } catch (encryptionError) {
          console.error("Error encrypting password:", encryptionError);
          throw encryptionError;
        }
      }
      return "";
    }
  };

  // Get secret key value for submission (handles both add and edit modes)
  const getSecretKeyForSubmission = async (): Promise<string> => {
    if (isEditMode) {
      if (
        isSecretKeyEditing &&
        formData.secret_key &&
        formData.secret_key.trim() !== ""
      ) {
        try {
          return await encryptPassword(formData.secret_key);
        } catch (encryptionError) {
          console.error("Error encrypting secret key:", encryptionError);
          throw encryptionError;
        }
      } else {
        return existingEncryptedSecretKey;
      }
    } else {
      if (formData.secret_key && formData.secret_key.trim() !== "") {
        try {
          return await encryptPassword(formData.secret_key);
        } catch (encryptionError) {
          console.error("Error encrypting secret key:", encryptionError);
          throw encryptionError;
        }
      }
      return "";
    }
  };

  return {
    formData,
    isPasswordEditing,
    isSecretKeyEditing,
    passwordLoading,
    secretKeyLoading,
    handleInputChange,
    handlePasswordEditClick,
    handleSecretKeyEditClick,
    getPasswordForSubmission,
    getSecretKeyForSubmission,
  };
};
